import { FC, useEffect, useState } from "react";
import {
  ActivityTabContentProps,
  addOrEditPermissions,
  getDefaultModalConfig,
} from "./ActivityTabConstants";
import ModalNewPaymentSelector from "../PaymentsTab/PaymentSelector/ModalNewPaymentSelector";
import { PaymentSelectorConfig } from "../PaymentsTab/PaymentSelector/PaymentSelectorTypes";
import ModalAddPayment from "../PaymentsTab/CheckPayment/ModalAddPayment";
import ModalCreditCardPayment from "../PaymentsTab/CreditCardPayment/ModalCreditCardPayment";
import { PaymentModalConfigurationProps } from "../PaymentsTab/InsuredPayments";
import ModalCreditCardPaymentRedirect from "../PaymentsTab/CreditCardPaymentRedirect/ModalCreditCardPaymentRedirect";
import ModalAchPayment from "../PaymentsTab/AchPayment/ModalAchPayment";
import { AccountTableDto } from "../../../../../dtos/account-table-dto";
import { useApiGet, usePermissions } from "../../../../../hooks";
import { ModalConfigBillingItemProps } from "./BillingItem/TypesBillingItem";
import ModalBillingItem from "./BillingItem/ModalBillingItem";
import ButtonsBillingActivityTable from "./BillingActivityTable/ButtonsBillingActivityTable/ButtonsBillingActivityTable";
import TableHeaderTotals from "./BillingActivityTable/ButtonsBillingActivityTable/TableHeaderTotals";
import style from "./BillingActivityStyles.module.css";
import BillingActivityTableWrapper from "./BillingActivityTable/BillingActivityTableWrapper";
import { BillingActivitySummaryDto } from "../../../../../dtos/billing-activity-summary-dto";
import { isAPITotallyComplete } from "../../../../../utilities/apiFunctions";
import { INSURED_ATOM_KEY } from "../../../../../utilities/queryStringsHash";
import { useAtomFamily } from "../../../../../hooks/useAtomFamily";
import { GlobalInsuredAtomFamily } from "../../../InsuredAtoms";
import {
  EditEntryActionProps,
  NewEntryActionProps,
} from "./BillingActivityTable/TypesBillingActivityTable";

const ActivityTabContent: FC<ActivityTabContentProps> = ({
  insuredId,
  tabKey,
  hasAch = false,
  hasCreditCard = false,
}) => {
  const { hasPermission } = usePermissions(addOrEditPermissions);

  const [refreshTable, setRefreshTable] = useState<boolean>(true);
  const [modalConfigBillingItem, setModalConfigBillingItem] =
    useState<ModalConfigBillingItemProps>({
      showModalBillingItem: false,
      insuredId: insuredId,
      hasPermission: hasPermission,
      transactionId: null,
      activityType: null,
      refreshBillingActivityTable: false,
    });

  const { getAtom } = useAtomFamily(
    GlobalInsuredAtomFamily(`${INSURED_ATOM_KEY} ${tabKey}`)
  );

  const insuredAtomFamilyValues = getAtom();
  const policyIdFromRecoil =
    insuredAtomFamilyValues?.billingPolicyFilterSelected?.policyId ?? "0";
  const validatedPolicyIdFromRecoil =
    policyIdFromRecoil !== "" ? parseInt(policyIdFromRecoil) : 0;

  const [currentPolicy, setCurrentPolicy] = useState<number | null>(
    validatedPolicyIdFromRecoil
  );
  const { dispatchGet, responseGet } = useApiGet<BillingActivitySummaryDto[]>(
    `api/Activity/GetBillingActivityTotals?insuredId=${insuredId}&policyId=${currentPolicy}`
  );
  useEffect(() => {
    if (currentPolicy !== null) {
      setRefreshTable(false);
    }
  }, [currentPolicy]);

  useEffect(() => {
    if (isAPITotallyComplete(responseGet)) {
      setRefreshTable(true);
    }
  }, [responseGet]);

  useEffect(() => {
    if (refreshTable === false) {
      dispatchGet();
    }
  }, [refreshTable]);

  const [accountTableData, setAccountTableData] = useState<
    AccountTableDto | undefined | null
  >(null);

  const [paymentModalConfig, setPaymentModalConfig] =
    useState<PaymentModalConfigurationProps>({
      ...getDefaultModalConfig(),
      hasAch,
      hasCreditCard,
    });

  const [paymentSelectorModalConfig, setPaymentSelectorModalConfig] =
    useState<PaymentSelectorConfig>({
      isOpen: false,
      hasAch,
      hasCreditCard,
      paymentProcessorFunction: null,
    });

  const openPaymentSelectorModal = () => {
    setPaymentSelectorModalConfig({
      ...paymentSelectorModalConfig,
      isOpen: true,
    });
  };

  const closePaymentSelectorModal = () => {
    setAccountTableData(null);
    setPaymentSelectorModalConfig({
      ...paymentSelectorModalConfig,
      isOpen: false,
    });
  };

  useEffect(() => {
    if (modalConfigBillingItem?.refreshBillingActivityTable === true) {
      setRefreshTable(false);
      setModalConfigBillingItem({
        ...modalConfigBillingItem,
        refreshBillingActivityTable: false,
        showModalBillingItem: false,
      });
    }
  }, [modalConfigBillingItem?.refreshBillingActivityTable]);

  const setAccountTableDataAndConfig = (accountTableData) => {
    setAccountTableData(accountTableData);
    setPaymentSelectorModalConfig({
      ...paymentSelectorModalConfig,
      paymentProcessorFunction:
        accountTableData?.paymentProcessorFunction ?? null,
    });
  };

  const setModalConfigForNewEntryAction = (
    newEntryModalConfig: NewEntryActionProps
  ) =>
    setModalConfigBillingItem({
      ...modalConfigBillingItem,
      showModalBillingItem: newEntryModalConfig.showModalBillingItem,
      activityType: newEntryModalConfig.activityType,
    });

  const setModalConfigForEditEntryAction = (
    editEntryModalConfig: EditEntryActionProps
  ) =>
    setModalConfigBillingItem({
      ...modalConfigBillingItem,
      showModalBillingItem: editEntryModalConfig.showModalBillingItem,
      activityType: editEntryModalConfig.activityType,
      transactionId: editEntryModalConfig.transactionId,
    });

  return (
    <div className={style.billing_activity_tab_container}>
      {refreshTable === true && (
        <BillingActivityTableWrapper
          tabKey={tabKey}
          insuredId={insuredId}
          currentPolicy={currentPolicy}
          setCurrentPolicy={setCurrentPolicy}
          setModalConfigForEditEntryAction={setModalConfigForEditEntryAction}
          customTableHeader={
            <ButtonsBillingActivityTable
              openPaymentSelectorModal={openPaymentSelectorModal}
              setModalConfigForNewEntryAction={setModalConfigForNewEntryAction}
              hasPermission={hasPermission}
              billingActivityTotals={
                <TableHeaderTotals
                  billingActivityTotals={responseGet?.responseData ?? []}
                />
              }
            />
          }
        />
      )}
      <ModalNewPaymentSelector
        configuration={paymentSelectorModalConfig}
        closeModal={closePaymentSelectorModal}
        openCheckModal={() =>
          setPaymentModalConfig({
            ...paymentModalConfig,
            isOpenModalAddNewPayment: true,
          })
        }
        openCreditCardModal={() =>
          setPaymentModalConfig({
            ...paymentModalConfig,
            isOpenModalCreditCardPayment: true,
          })
        }
        openCreditCardRedirectModal={() =>
          setPaymentModalConfig({
            ...paymentModalConfig,
            isOpenModalCreditCardPaymentRedirect: true,
          })
        }
        openAchModal={() =>
          setPaymentModalConfig({
            ...paymentModalConfig,
            isOpenAchPaymentModal: true,
          })
        }
        insuredId={insuredId}
        setAccountTableData={setAccountTableDataAndConfig}
        hasSelectedPaymentOptionCallback={() => {
          closePaymentSelectorModal();
        }}
      />
      {accountTableData ? (
        <>
          <ModalAddPayment
            insuredId={insuredId}
            hasAch={paymentModalConfig.hasAch ?? false}
            hasCreditCard={paymentModalConfig.hasCreditCard ?? false}
            showModal={paymentModalConfig.isOpenModalAddNewPayment}
            isOpenModal={(close) => {
              setPaymentModalConfig?.({
                ...paymentModalConfig,
                isOpenModalAddNewPayment: close,
              });
              setRefreshTable(false);
            }}
            accountTableData={accountTableData}
          />

          <ModalCreditCardPayment
            insuredId={insuredId}
            showModal={paymentModalConfig.isOpenModalCreditCardPayment}
            isOpenModal={(close) => {
              setPaymentModalConfig?.({
                ...paymentModalConfig,
                isOpenModalCreditCardPayment: close,
              });
              setRefreshTable(false);
            }}
            accountTableData={accountTableData}
          />
          <ModalCreditCardPaymentRedirect
            insuredId={insuredId}
            showModal={paymentModalConfig.isOpenModalCreditCardPaymentRedirect}
            isOpenModal={(close) => {
              setPaymentModalConfig?.({
                ...paymentModalConfig,
                isOpenModalCreditCardPaymentRedirect: close,
              });
              setRefreshTable(false);
            }}
            accountTableData={accountTableData}
          />
          <ModalAchPayment
            insuredId={insuredId}
            showModal={paymentModalConfig.isOpenAchPaymentModal}
            isOpenModal={(close) =>
              setPaymentModalConfig?.({
                ...paymentModalConfig,
                isOpenAchPaymentModal: close,
              })
            }
            onSaveSuccessful={() => setRefreshTable(false)}
            accountTableData={accountTableData}
          />
        </>
      ) : null}

      <ModalBillingItem
        currentPolicy={currentPolicy}
        setModalConfigBillingItem={setModalConfigBillingItem}
        modalConfigBillingItem={modalConfigBillingItem}
      />
    </div>
  );
};

export default ActivityTabContent;
